import { API_CONFIG, ApiResponse, ApiError } from '../config/api';
import { getFingerprint } from '../utils/fingerprint';

class ApiService {
  private baseURL: string;
  private defaultHeaders: Record<string, string>;

  constructor() {
    this.baseURL = API_CONFIG.BASE_URL;
    this.defaultHeaders = API_CONFIG.DEFAULT_HEADERS;
  }

  private getUserId(): string | undefined {
    try {
      const userStr = localStorage.getItem('auth_user');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user.id;
      }
    } catch (e) {
      console.error('Error getting user ID:', e);
    }
    return undefined;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    const url = `${this.baseURL}${endpoint}`;

    // Get fingerprint (uses user ID if authenticated, otherwise guest fingerprint)
    const userId = this.getUserId();
    const fingerprint = getFingerprint(userId);

    // Destructure headers from options to avoid overwriting them with ...options spread
    const { headers: optionsHeaders, ...restOptions } = options;

    const config: RequestInit = {
      ...restOptions,
      credentials: 'include',
      headers: {
        ...this.defaultHeaders,
        'x-fingerprint': fingerprint,
        ...optionsHeaders,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ API Error Response:', errorText);
        throw new ApiError(
          `HTTP error! status: ${response.status}`,
          response.status
        );
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('❌ API Request Failed:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(
        error instanceof Error ? error.message : 'An unknown error occurred'
      );
    }
  }

  async get<T>(
    endpoint: string,
    params?: Record<string, string | number>,
    authToken?: string,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    let url = endpoint;

    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      url += `?${searchParams.toString()}`;
    }

    const headers: Record<string, string> = {};
    if (authToken && authToken !== '__cookie_session__') {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    if (extraHeaders) {
      Object.assign(headers, extraHeaders);
    }

    return this.request<T>(url, {
      method: 'GET',
      headers: Object.keys(headers).length > 0 ? headers : undefined
    });
  }

  async post<T>(
    endpoint: string,
    data?: any,
    authToken?: string,
    extraHeaders?: Record<string, string>,
  ): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {};
    if (authToken && authToken !== '__cookie_session__') {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    if (extraHeaders) {
      Object.assign(headers, extraHeaders);
    }

    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
  }

  async put<T>(endpoint: string, data?: any, authToken?: string): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {};
    if (authToken && authToken !== '__cookie_session__') {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
  }

  async patch<T>(endpoint: string, data?: any, authToken?: string): Promise<ApiResponse<T>> {
    const headers: Record<string, string> = {};
    if (authToken && authToken !== '__cookie_session__') {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async download(endpoint: string, fileName: string): Promise<void> {
    const url = `${this.baseURL}${endpoint}`;
    const userId = this.getUserId();
    const fingerprint = getFingerprint(userId);

    try {
      const response = await fetch(url, {
        headers: {
          ...this.defaultHeaders,
          'x-fingerprint': fingerprint,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }
}

export const apiService = new ApiService();
export default apiService;
